import { Box, Checkbox, Stack, Typography, useTheme } from "@mui/material";
import { FoodsTags } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { ConsentGateItem } from "src/api/client-api/consent/useGetConsentGateItems";
import { getConsentCategoryConfig, hasKnownConsentCategory } from "src/components/Consent/consentCategoryConfig";
import { getConsentDisplayTitle } from "src/components/Consent/consentDisplay";

type DocumentRowProps = {
    item: ConsentGateItem;
    isChecked: boolean;
    onToggle: () => void;
    onViewDocument: () => void;
};

const breakLongTextSx = {
    hyphens: "auto",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
} as const;

export const DocumentRow = ({ item, isChecked, onToggle, onViewDocument }: DocumentRowProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const categoryConfig = getConsentCategoryConfig(item.category);
    const categoryLabel = t(categoryConfig.labelKey, categoryConfig.labelFallback);
    const showCategory = hasKnownConsentCategory(item.category);
    const displayTitle = getConsentDisplayTitle(item.title, item.version);

    return (
        <Box
            sx={{
                width: "100%",
                p: theme.spacing(1.75, 2),
                borderRadius: theme.spacing(1),
                border: `1px solid ${isChecked ? theme.palette.primary.main : theme.custom.grey[500]}`,
                bgcolor: isChecked ? theme.palette.primary[50] : "rgba(42, 43, 39, 0.025)",
                transition: "border-color 0.15s, background-color 0.15s",
                userSelect: "none",
            }}
        >
            <Box
                sx={{
                    columnGap: theme.spacing(1.5),
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr)",
                    rowGap: theme.spacing(0.5),
                    width: "100%",
                }}
            >
                <Box sx={{ alignItems: "center", alignSelf: "stretch", display: "flex", gridColumn: 1, gridRow: 1 }}>
                    <Checkbox
                        checked={isChecked}
                        inputProps={{
                            "aria-label": displayTitle,
                        }}
                        onChange={onToggle}
                        size="small"
                        sx={{
                            p: 0,
                            flexShrink: 0,
                            color: theme.custom.grey[1500],
                            "&.Mui-checked": {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                </Box>
                <Typography
                    component="button"
                    type="button"
                    onClick={onViewDocument}
                    sx={{
                        background: "none",
                        border: "none",
                        color: theme.custom.grey[2800],
                        cursor: "pointer",
                        display: "block",
                        fontFamily: "inherit",
                        fontSize: "14px",
                        fontWeight: 600,
                        gridColumn: 2,
                        gridRow: 1,
                        lineHeight: 1.35,
                        maxWidth: "100%",
                        minWidth: 0,
                        p: 0,
                        textAlign: "left",
                        textDecoration: "underline",
                        textDecorationSkipInk: "none",
                        textUnderlineOffset: "2px",
                        whiteSpace: "normal",
                        ...breakLongTextSx,
                        "&:hover": { color: theme.palette.primary.main },
                    }}
                >
                    {displayTitle}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ gridColumn: 2, minWidth: 0 }}>
                    {showCategory && <FoodsTags label={categoryLabel} variant="tags-grey" />}
                    {item.version && <FoodsTags label={`v${item.version}`} variant="tags-grey" />}
                    <FoodsTags label={t("consent_gate.required_tag", "Required")} variant="tags-red" />
                </Stack>
                <Typography
                    variant="body2"
                    sx={{
                        color: theme.custom.grey[2000],
                        gridColumn: 2,
                        lineHeight: 1.5,
                        ...breakLongTextSx,
                    }}
                >
                    {t("consent_gate.click_to_read", "Cliquez sur le titre pour lire le document")}
                </Typography>
            </Box>
        </Box>
    );
};
